import React, { useEffect, useState } from 'react'
import { api, apiBlob } from '../lib/api'
import { CredentialingBadge } from './ui'
import type { CredentialingEvent, Provider, ProviderDocument } from '../lib/types'

const EVENT_LABELS: Record<string, string> = {
  credentialing_submitted: 'Submitted for review',
  credentialing_in_review: 'Marked in review',
  credentialing_approved: 'Approved',
  credentialing_rejected: 'Rejected',
  credentialing_action_required: 'Action required',
  credentialing_note_added: 'Note added',
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  license: 'License',
  resume: 'Resume',
  board_certification: 'Board certification',
  malpractice_insurance: 'Malpractice insurance',
}

const EDITABLE_STATUSES = ['unverified', 'rejected', 'action_required']

export function ProviderCredentialingPanel({ provider, editable, onUpdated }: {
  provider: Provider
  editable: boolean
  onUpdated: () => void
}) {
  const [history, setHistory] = useState<CredentialingEvent[]>([])
  const [documents, setDocuments] = useState<ProviderDocument[]>([])
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [npiNumber, setNpiNumber] = useState(provider.npi_number || '')
  const [licenseNumber, setLicenseNumber] = useState(provider.license_number || '')
  const [licenseState, setLicenseState] = useState(provider.license_state || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function loadHistory() {
    const result = await api(`/providers/${provider.id}/credentialing/history`)
    setHistory(result)
  }

  async function loadDocuments() {
    const result = await api(`/providers/${provider.id}/documents`)
    setDocuments(result)
  }

  useEffect(() => { loadHistory(); loadDocuments() }, [provider.id])

  async function viewDocument(doc: ProviderDocument) {
    setViewingId(doc.id)
    try {
      const blob = await apiBlob(`/providers/${provider.id}/documents/${doc.id}/download`)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      // Give the new tab a moment to load the blob before revoking it.
      setTimeout(() => URL.revokeObjectURL(url), 30000)
    } finally {
      setViewingId(null)
    }
  }

  const canEdit = editable && EDITABLE_STATUSES.includes(provider.credentialing_status)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api(`/providers/${provider.id}/credentialing`, {
        method: 'PATCH',
        body: JSON.stringify({ npi_number: npiNumber || null, license_number: licenseNumber || null, license_state: licenseState || null }),
      })
      onUpdated()
      loadHistory()
    } catch {
      setError('Could not save credentialing info.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <CredentialingBadge status={provider.credentialing_status} />
        {provider.verified_at && (
          <span className="muted" style={{ fontSize: 12.5 }}>
            {provider.credentialing_status === 'approved' ? 'Approved' : 'Reviewed'} {new Date(provider.verified_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {canEdit ? (
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="field"><label>NPI number</label><input className="inp" value={npiNumber} onChange={(e) => setNpiNumber(e.target.value)} placeholder="10-digit NPI" /></div>
            <div className="field"><label>License number</label><input className="inp" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} /></div>
            <div className="field"><label>License state</label><input className="inp" value={licenseState} onChange={(e) => setLicenseState(e.target.value)} placeholder="AB" /></div>
          </div>
          {error && <div className="form-error">{error}</div>}
          <button className="btn small" style={{ marginTop: 10 }} disabled={busy}>
            {busy ? 'Saving…' : provider.credentialing_status === 'unverified' ? 'Submit for review' : 'Resubmit for review'}
          </button>
        </form>
      ) : (
        <div className="row-list">
          <div className="row"><span className="muted">NPI number</span><span>{provider.npi_number || '—'}</span></div>
          <div className="row"><span className="muted">License number</span><span>{provider.license_number || '—'}</span></div>
          <div className="row"><span className="muted">License state</span><span>{provider.license_state || '—'}</span></div>
        </div>
      )}

      {provider.verification_notes && (
        <div className="card" style={{ marginTop: 12 }}>
          <p className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Latest reviewer note</p>
          <p style={{ fontSize: 13.5, margin: 0 }}>{provider.verification_notes}</p>
        </div>
      )}

      <div className="section-block">
        <div className="section-head"><h2>Uploaded documents</h2></div>
        {documents.length === 0 ? (
          <div className="empty">No documents uploaded yet.</div>
        ) : (
          <div className="row-list">
            {documents.map((d) => (
              <div className="row" key={d.id}>
                <span>
                  {DOCUMENT_TYPE_LABELS[d.document_type] || d.document_type}
                  <span className="muted" style={{ marginLeft: 8, fontSize: 12.5 }}>{d.filename}</span>
                </span>
                <button className="btn small outline" disabled={viewingId === d.id} onClick={() => viewDocument(d)}>
                  {viewingId === d.id ? 'Opening…' : 'View'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Verification history</h2></div>
        {history.length === 0 ? (
          <div className="empty">No credentialing activity yet.</div>
        ) : (
          <div className="timeline">
            {history.map((h) => (
              <div className="timeline-item" key={h.id}>
                <div className="ti-type">{EVENT_LABELS[h.event_type] || h.event_type}{h.payload_json?.notes ? `: ${h.payload_json.notes}` : ''}</div>
                <div className="ti-time">{new Date(h.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
